import {
  MultiValuesNode,
  NodeWithGroupInfo,
  SingleValueNode,
  ValuesObject,
} from '../../types/chartDataTypes';

export type ValueMappingFunction = (entry: SingleValueNode) => ValuesObject;

export function getValue(node: MultiValuesNode, dataLabel: string): number | null {
  const { values } = node;
  if (values && values[dataLabel]) {
    return values[dataLabel];
  }
  return null;
}

export function mapNode<
  Tin extends SingleValueNode | MultiValuesNode, Tout extends SingleValueNode | MultiValuesNode,
>(
  node: Tin,
  setValueFunction: (input: Tin, output: Tout) => void,
): Tout {
  const result = {
    name: node.name,
    totalLabel: node.totalLabel,
  } as Tout;
  if (node.entries) {
    // @ts-ignore
    result.entries = node.entries.map((entry) => mapNode(entry, setValueFunction));
  }
  setValueFunction(node, result);
  return result;
}

export const asMultiValueNode = (
  node: SingleValueNode,
  valueMapping: string | ValueMappingFunction,
): MultiValuesNode => mapNode(node, (inputNode: SingleValueNode, resultNode: MultiValuesNode) => {
  if (typeof valueMapping === 'function') {
    resultNode.values = valueMapping(inputNode);
  } else if (inputNode.value != null) {
    resultNode.values = {
      [valueMapping]: inputNode.value,
    };
  }
});

export const asSingleValueNode = (
  node: MultiValuesNode,
  dataColumn: string,
): SingleValueNode => mapNode(node, (inputNode: MultiValuesNode, resultNode: SingleValueNode) => {
  resultNode.value = getValue(inputNode, dataColumn);
});

export const asSingleValueNodes = (
  nodes: MultiValuesNode[] | undefined,
  dataColumn: string,
): SingleValueNode[] => (
  nodes
    ? nodes.map((node) => asSingleValueNode(node, dataColumn))
    : []
);

export function getBottomGroups<T extends SingleValueNode | MultiValuesNode>(node: T): T[] {
  let result: T[] = [];
  if (node.entries?.length) {
    if (node.entries[0].entries?.length) {
      node.entries.forEach((entry) => {
        result = result.concat(getBottomGroups(entry));
      });
    } else {
      // this is a bottom group!
      result.push(node);
    }
  }
  return result;
}

/**
 * Will return the node structure without the leaf nodes.
 * @param node
 */
export function withoutLeafNodes<T extends SingleValueNode | MultiValuesNode>(node: T): T {
  if (node.entries?.length) {
    const { entries: existingEntries, ...nodeProps } = node;
    const result: T = nodeProps as T;
    // @ts-ignore
    const entries = existingEntries.map(withoutLeafNodes).filter((entry) => entry);
    if (entries.length) {
      result.entries = entries;
    }
    return result;
  }
  return null;
}

/**
 * Will return all the leaf nodes in one list.
 * The group nodes are skipped.
 * @param node The root node
 */
export function getLeafNodes<T extends SingleValueNode | MultiValuesNode>(node: T): T[] {
  if (node.entries?.length) {
    const { entries } = node;
    // @ts-ignore
    return entries.reduce((result, entry) => {
      result.push(...getLeafNodes(entry));
      return result;
    }, []);
  }
  return [node];
}

export function withGroupInfo<T extends SingleValueNode | MultiValuesNode>(node: T, groupNames: string[] = []): T {
  const { entries: existingEntries, ...nodeProps } = node;
  // @ts-ignore
  const entries = existingEntries?.map((entry) => {
    const groupName = node.totalLabel || node.name;
    return withGroupInfo(entry, groupName ? groupNames.concat(groupName) : groupNames);
  });
  const result: T = {
    ...nodeProps,
    entries,
  } as T;
  (result as NodeWithGroupInfo).groupNames = groupNames;
  return result;
}

export function findEntryWithName<T extends SingleValueNode | MultiValuesNode>(node: T, searchName: string): T {
  if (node.name === searchName || node.totalLabel === searchName) {
    return node;
  }
  if (node.entries?.length) {
    for (let i = 0; i < node.entries.length; i += 1) {
      const match = findEntryWithName(node.entries[i], searchName);
      if (match) {
        return match as T;
      }
    }
  }
  return null;
}
